import { isScreenLoadEvent, type AttachActionCmd, type DslCommand } from "./workflow-dsl-parser";

/**
 * KOA-18431 — A FILTER IS ONE THING, AND A REFUSAL THAT TAKES ONE LINE OUT OF IT LEAVES A WRECK.
 *
 * Live `5609cb0d`: a read was attached to the task screen's LOAD and fed from a SelectBox on that
 * same screen. The rule that catches it (`composition-screen-load-input-from-control`) refused the
 * `ARG` — correctly, it can never carry a value — and everything else stood: the read still ran at
 * open with the parameter empty, so the list was permanently empty, and the SelectBox was left with
 * five choices and nobody listening. Refusing a leaf does not repair a construct; it only decides
 * which half of a broken thing survives.
 *
 * The intent is not ambiguous, and the script says all of it: a read whose input comes from a
 * control is a FILTER, and a filter runs when the control changes. That is the shape the grammar
 * teaches, and it is derivable — so it is made, not demanded. The `ACTION` line's host moves from
 * the step's body to the control, and its event from the screen-load one to `onChange`.
 *
 * Deliberately narrow, because a repair that guesses is worse than a refusal that explains:
 *
 *   - Only a read on a SCREEN-LOAD event of a STEP (the body). A read already on a component's own
 *     event is the author's choice and is left alone.
 *   - Only when every control-fed argument of that read names the SAME control. Two controls mean
 *     two triggers, and which one the author meant is not in the script — that one still goes to
 *     the validator, which names both.
 *   - The event is not checked against the catalog here; this module is pure and holds no project.
 *     A control that does not declare `onChange` is refused by the platform's own event gate at
 *     apply time, with the list of the events it does declare.
 */

/** One rewritten `ACTION`, reported so a repair is never silent. */
export interface ICompositionRepairNote {
  /** The line the repair rewrote. */
  line: number;
  message: string;
}

export interface ICompositionRepairResult {
  commands: DslCommand[];
  notes: ICompositionRepairNote[];
}

/** The event a filtering read belongs on. */
const FILTER_EVENT: string = "onChange";

function isActionCommand(command: DslCommand): command is AttachActionCmd {
  return command.kind === "ACTION";
}

/**
 * Which control feeds each action alias, when exactly one does.
 *
 * `null` marks an alias fed from MORE than one control — the ambiguous case, kept apart from
 * "fed from none" so the caller can leave it to the validator instead of choosing a trigger.
 */
function controlByActionAlias(commands: readonly DslCommand[]): Map<string, string | null> {
  const byAlias: Map<string, string | null> = new Map<string, string | null>();
  for (const command of commands) {
    if (command.kind !== "ARG") continue;
    if (command.source.kind !== "componentValue") continue;
    const existing: string | null | undefined = byAlias.get(command.actionAlias);
    if (existing === undefined) byAlias.set(command.actionAlias, command.source.alias);
    else if (existing !== command.source.alias) byAlias.set(command.actionAlias, null);
  }
  return byAlias;
}

export function repairScreenLoadFilterReads(commands: readonly DslCommand[]): ICompositionRepairResult {
  const feeders: Map<string, string | null> = controlByActionAlias(commands);
  if (feeders.size === 0) return { commands: [...commands], notes: [] };

  const notes: ICompositionRepairNote[] = [];
  const repaired: DslCommand[] = commands.map((command: DslCommand): DslCommand => {
    if (!isActionCommand(command)) return command;
    if (command.host.kind !== "step") return command;
    if (!isScreenLoadEvent(command.eventName)) return command;
    const alias: string | undefined = command.actionAlias;
    if (alias === undefined) return command;
    const control: string | null | undefined = feeders.get(alias);
    if (control === undefined || control === null) return command;

    notes.push({
      line: command.line,
      message:
        `"${command.actionName}" was attached to ${command.host.stepRef}'s ${command.eventName}, and one of its ` +
        `inputs comes from ${control} — a control that is still empty while the screen comes up. A read fed from ` +
        `a control is a filter, so it was moved to that control's own \`${FILTER_EVENT}\`, where the value exists. ` +
        `If the list must also show something BEFORE a choice is made, give the step's body its own read with ` +
        `arguments the process carries.`
    });
    return { ...command, host: { kind: "alias", alias: control }, eventName: FILTER_EVENT };
  });

  return { commands: repaired, notes };
}
